import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
import { orderStatusSchema, paymentStatusSchema } from "./orders.js";
import { AVAILABILITY, publicImageSchema, storefrontHostSchema } from "./storefront.js";
import { cartSchema } from "./storefront-checkout.js";
/**
 * Shopper accounts and order tracking (B-78, SF-10; DG-01 accounts with passwords, docs/specs/shopper-accounts.md):
 * sign in, see your own orders in this shop; or follow the signed tracking link from the order SMS without signing in.
 */
const tag = { tag: "storefront" };
const pub = { public: true };
const host = storefrontHostSchema;
/** The shopper's session secret (base64url, 43 chars); only its SHA-256 is stored. */
const sessionToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const shopperOrderSummarySchema = z.object({
    number: z.string(),
    placedAt: z.iso.datetime(),
    status: orderStatusSchema,
    paymentStatus: paymentStatusSchema,
    totalPaisa: z.int(),
    itemCount: z.int(),
});
export const shopperOrderSchema = shopperOrderSummarySchema.extend({
    deliveryChargePaisa: z.int(),
    lines: z.array(z.object({ name: z.string(), quantity: z.string(), totalPaisa: z.int() })),
    /** Status steps, oldest first. */
    timeline: z.array(z.object({ status: orderStatusSchema, at: z.iso.datetime() })),
    /** Where it goes (district/area/line), for the shopper to check. */
    address: z.string(),
    /** DG-03: the parcel with the courier, once booked. */
    shipment: z
        .object({
        courier: z.string(),
        consignmentId: z.string().nullable(),
        trackingUrl: z.string().nullable(),
        status: z.string().nullable(),
    })
        .nullable()
        .default(null),
    /** DG-03: the shopper may still cancel it (received or confirmed, nothing paid online). */
    canCancel: z.boolean().default(false),
});
// DG-01 (docs/specs/shopper-accounts.md): accounts with a password, by email or mobile number ---------------------
/** An email (has `@`) or a Bangladeshi mobile number, as the shopper typed it. */
const identifier = z.string().trim().min(3).max(254);
/** 8–128 characters; the API also refuses common, digit-only and sequence passwords (identity's rules). */
const password = z.string().min(8).max(128);
const captcha = z.string().trim().min(1).max(2048);
/** The visitor's IP as the storefront server saw it (rate limits); never stored. */
const ip = z.string().max(64).default("");
/** DG-03: the shopper's browser as the storefront saw it, for the device list; never used to decide anything. */
const userAgent = z.string().max(400).default("");
/** A ticket from `verifyShopOtp` or `shopperVerifyEmailCode`. */
const ticket = z.string().min(1).max(100);
export const shopperSessionSchema = z.object({ token: z.string(), expiresAt: z.iso.datetime() });
/** The signed-in shopper's own account. */
export const shopperAccountSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    email: z.string().nullable(),
    emailVerified: z.boolean(),
    phone: z.string().nullable(),
    phoneVerified: z.boolean(),
    hasPassword: z.boolean(),
    /** DG-02: a Google account is linked. */
    google: z.boolean().default(false),
    /** DG-03: the shopper's language and offers by SMS / e-mail. */
    locale: z.enum(["en", "bn"]).default("en"),
    marketingOptIn: z.boolean().default(false),
});
export const SHOPPER_SIGN_IN_METHODS = ["password", "phone_code", "google"];
export const shopperIdentifySchema = z.object({
    exists: z.boolean(),
    kind: z.enum(["email", "phone"]),
    /** How an existing account can sign in; empty when there is none. */
    methods: z.array(z.enum(SHOPPER_SIGN_IN_METHODS)),
    /** For a new account: whether this shop takes sign-ups with this kind, and whether it asks for a code first. */
    signUp: z.object({ allowed: z.boolean(), codeRequired: z.boolean() }),
});
export const shopperIdentify = defineEndpoint({
    ...tag,
    name: "shopperIdentify",
    method: "POST",
    path: "/v1/storefront/account/identify",
    access: pub,
    summary: "First sign-in step: is there an account for this email or mobile number, and how does it sign in",
    body: z.object({ host, identifier, ip }).strict(),
    status: 200,
    response: shopperIdentifySchema,
    errors: ["VALIDATION_FAILED", "RATE_LIMITED"],
});
export const shopperSignUp = defineEndpoint({
    ...tag,
    name: "shopperSignUp",
    method: "POST",
    path: "/v1/storefront/account/sign-up",
    access: pub,
    summary: "Create an account with an email or mobile number and a password; signs it in",
    body: z
        .object({
        host,
        name: z.string().trim().min(1).max(120),
        identifier,
        password,
        /** Needed when the shop asks for a code at sign-up for this kind of identifier. */
        ticket: ticket.optional(),
        captcha,
        ip,
        userAgent,
    })
        .strict(),
    status: 201,
    response: shopperSessionSchema,
    errors: [
        "VALIDATION_FAILED",
        "ACCOUNT_EXISTS",
        "SIGN_UP_OFF",
        "CODE_REQUIRED",
        "INVALID_OTP",
        "BOT_CHECK_FAILED",
        "RATE_LIMITED",
    ],
});
export const shopperPasswordSignIn = defineEndpoint({
    ...tag,
    name: "shopperPasswordSignIn",
    method: "POST",
    path: "/v1/storefront/account/sign-in/password",
    access: pub,
    summary: "Sign in with an email or mobile number and the password (10 failures in 15 minutes lock the account)",
    body: z.object({ host, identifier, password, ip, userAgent }).strict(),
    status: 200,
    response: shopperSessionSchema,
    errors: ["INVALID_CREDENTIALS", "ACCOUNT_LOCKED", "RATE_LIMITED"],
});
export const shopperSignIn = defineEndpoint({
    ...tag,
    name: "shopperSignIn",
    method: "POST",
    path: "/v1/storefront/account/sign-in",
    access: pub,
    summary: "Sign in with a mobile code: a phone ticket (from the OTP) for the account with that number, when the shop allows it",
    body: z.object({ host, phone: z.string().min(10).max(20), ticket, userAgent }).strict(),
    status: 200,
    response: shopperSessionSchema,
    errors: ["INVALID_OTP", "INVALID_CREDENTIALS", "CODE_SIGN_IN_OFF"],
});
/** A Google Identity Services ID token (a JWT) from the "Continue with Google" button. */
const credential = z.string().min(20).max(4096);
export const shopperGoogleSignIn = defineEndpoint({
    ...tag,
    name: "shopperGoogleSignIn",
    method: "POST",
    path: "/v1/storefront/account/sign-in/google",
    access: pub,
    summary: "Sign in or sign up with Google (the shop's own client): the account with this Google ID, else the one with the same verified email, else a new one",
    body: z.object({ host, credential, ip, userAgent }).strict(),
    status: 200,
    response: shopperSessionSchema,
    errors: ["INVALID_GOOGLE_TOKEN", "GOOGLE_SIGN_IN_OFF", "INVALID_CREDENTIALS", "RATE_LIMITED"],
});
export const shopperLinkGoogle = defineEndpoint({
    ...tag,
    name: "shopperLinkGoogle",
    method: "POST",
    path: "/v1/storefront/account/me/google",
    access: pub,
    summary: "Link a Google account to the signed-in account",
    body: z.object({ host, token: sessionToken, credential }).strict(),
    status: 200,
    response: shopperAccountSchema,
    errors: ["UNAUTHENTICATED", "INVALID_GOOGLE_TOKEN", "GOOGLE_SIGN_IN_OFF", "ACCOUNT_EXISTS"],
});
export const shopperUnlinkGoogle = defineEndpoint({
    ...tag,
    name: "shopperUnlinkGoogle",
    method: "POST",
    path: "/v1/storefront/account/me/google/unlink",
    access: pub,
    summary: "Remove the linked Google account (only when the account has a password)",
    body: z.object({ host, token: sessionToken }).strict(),
    status: 200,
    response: shopperAccountSchema,
    errors: ["UNAUTHENTICATED", "PASSWORD_REQUIRED"],
});
export const shopperSendEmailCode = defineEndpoint({
    ...tag,
    name: "shopperSendEmailCode",
    method: "POST",
    path: "/v1/storefront/account/email-code",
    access: pub,
    summary: "E-mail a 6-digit code that proves the address (bot check first; rate limited)",
    body: z.object({ host, email: z.email().max(254), captcha, ip }).strict(),
    status: 202,
    response: z.object({ expiresInSeconds: z.int(), resendAfterSeconds: z.int() }),
    errors: ["BOT_CHECK_FAILED", "RATE_LIMITED"],
});
export const shopperVerifyEmailCode = defineEndpoint({
    ...tag,
    name: "shopperVerifyEmailCode",
    method: "POST",
    path: "/v1/storefront/account/email-code/verify",
    access: pub,
    summary: "Check the e-mailed code; a ticket proves the address for 30 minutes",
    body: z.object({ host, email: z.email().max(254), code: z.string().regex(/^\d{6}$/) }).strict(),
    status: 200,
    response: z.object({ ticket: z.string(), expiresAt: z.iso.datetime() }),
    errors: ["INVALID_OTP", "RATE_LIMITED"],
});
export const shopperForgotPassword = defineEndpoint({
    ...tag,
    name: "shopperForgotPassword",
    method: "POST",
    path: "/v1/storefront/account/password/forgot",
    access: pub,
    summary: "Send a reset link (email) or code (mobile) if the account exists; the answer is the same either way",
    body: z.object({ host, identifier, captcha, ip }).strict(),
    status: 202,
    response: z.object({ channel: z.enum(["email", "phone"]) }),
    errors: ["VALIDATION_FAILED", "BOT_CHECK_FAILED", "RATE_LIMITED"],
});
export const shopperResetPassword = defineEndpoint({
    ...tag,
    name: "shopperResetPassword",
    method: "POST",
    path: "/v1/storefront/account/password/reset",
    access: pub,
    summary: "Choose a new password with the e-mailed link's token, or the phone and a code ticket; ends every session",
    body: z
        .object({
        host,
        password,
        /** From the e-mailed link. */
        token: sessionToken.optional(),
        /** With `ticket`: the phone the code was sent to. */
        phone: z.string().min(10).max(20).optional(),
        ticket: ticket.optional(),
    })
        .strict(),
    status: 204,
    errors: ["VALIDATION_FAILED", "INVALID_OTP"],
});
export const shopperMe = defineEndpoint({
    ...tag,
    name: "shopperMe",
    method: "POST",
    path: "/v1/storefront/account/me",
    access: pub,
    summary: "The signed-in shopper's account",
    body: z.object({ host, token: sessionToken }).strict(),
    status: 200,
    response: shopperAccountSchema,
    errors: ["UNAUTHENTICATED"],
});
export const shopperVerifyIdentifier = defineEndpoint({
    ...tag,
    name: "shopperVerifyIdentifier",
    method: "POST",
    path: "/v1/storefront/account/me/verify",
    access: pub,
    summary: "Mark the account's email or mobile number as verified with a code ticket for it",
    body: z.object({ host, token: sessionToken, kind: z.enum(["email", "phone"]), ticket }).strict(),
    status: 200,
    response: shopperAccountSchema,
    errors: ["UNAUTHENTICATED", "INVALID_OTP"],
});
export const shopperSignOut = defineEndpoint({
    ...tag,
    name: "shopperSignOut",
    method: "POST",
    path: "/v1/storefront/account/sign-out",
    access: pub,
    summary: "End the shopper session",
    body: z.object({ host, token: sessionToken }).strict(),
    status: 204,
});
export const shopperOrders = defineEndpoint({
    ...tag,
    name: "shopperOrders",
    method: "POST",
    path: "/v1/storefront/account/orders",
    access: pub,
    summary: "The signed-in shopper's online orders in this shop, newest first (last 50): placed while signed in, or with the account's verified mobile number",
    body: z.object({ host, token: sessionToken }).strict(),
    status: 200,
    response: z.object({
        /** The account's verified mobile number; empty when it has none. */
        phone: z.string(),
        account: shopperAccountSchema.optional(),
        data: z.array(shopperOrderSummarySchema),
    }),
    errors: ["UNAUTHENTICATED"],
});
export const shopperOrder = defineEndpoint({
    ...tag,
    name: "shopperOrder",
    method: "POST",
    path: "/v1/storefront/account/orders/{number}",
    access: pub,
    summary: "One of the signed-in shopper's orders (another phone's order is a 404)",
    params: z.object({ number: z.string().min(1).max(40) }),
    body: z.object({ host, token: sessionToken }).strict(),
    status: 200,
    response: shopperOrderSchema,
    errors: ["UNAUTHENTICATED"],
});
export const trackShopOrder = defineEndpoint({
    ...tag,
    name: "trackShopOrder",
    method: "POST",
    path: "/v1/storefront/track",
    access: pub,
    summary: "An order by the signed tracking link from its SMS (a wrong or other shop's link is a 404)",
    body: z.object({ host, token: z.string().min(10).max(200) }).strict(),
    status: 200,
    response: shopperOrderSchema,
});
// ST-11 (A7): order again --------------------------------------------------------------------------------------
/** The cart's secret (same shape as the session's). */
const cartToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
/**
 * One item the shopper ordered before, read again now: today's price and availability, the quantity of the latest
 * order it was in. Products the shop no longer sells are left out.
 */
export const orderAgainItemSchema = z.object({
    variantId: z.uuid(),
    productId: z.uuid(),
    slug: z.string(),
    name: z.string(),
    /** `Size: M · Colour: Red`; empty for a product without options. */
    optionLabel: z.string(),
    image: publicImageSchema.nullable(),
    /** Whole units, as the cart counts them (at least 1). */
    quantity: z.int().min(1),
    unitPricePaisa: z.int(),
    availability: z.enum(AVAILABILITY),
    lastOrderedAt: z.iso.datetime(),
});
export const shopperOrderAgain = defineEndpoint({
    ...tag,
    name: "shopperOrderAgain",
    method: "POST",
    path: "/v1/storefront/account/order-again",
    access: pub,
    summary: "Items from the signed-in shopper's recent orders in this shop, at today's prices (up to 12)",
    body: z.object({ host, token: sessionToken }).strict(),
    status: 200,
    response: z.object({ data: z.array(orderAgainItemSchema) }),
    errors: ["UNAUTHENTICATED"],
});
export const shopperOrderAgainAdd = defineEndpoint({
    ...tag,
    name: "shopperOrderAgainAdd",
    method: "POST",
    path: "/v1/storefront/account/order-again/cart",
    access: pub,
    summary: "Add every available order-again item to the cart (out-of-stock ones are skipped and counted)",
    body: z.object({ host, token: sessionToken, cartToken }).strict(),
    status: 200,
    response: z.object({ cart: cartSchema, added: z.int(), skipped: z.int() }),
    errors: ["UNAUTHENTICATED"],
});
//# sourceMappingURL=storefront-account.js.map